import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

function RoleName({ role, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(role.name);
  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <p className="font-semibold text-[#103080]" data-testid={`role-name-${role.id}`}>{role.name}</p>
        {canEdit && (
          <button onClick={() => { setName(role.name); setEditing(true); }} aria-label="Rename role" data-testid={`role-edit-${role.id}`}
            className="text-[#103080]/40 hover:text-[#0090D0] transition-colors duration-200"><Pencil size={13} /></button>
        )}
      </div>
    );
  }
  const save = async (e) => {
    e.preventDefault();
    if (await onSave({ ...role, name: name.trim() })) setEditing(false);
  };
  return (
    <form onSubmit={save} className="flex items-center gap-2">
      <Input autoFocus required value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg bg-white border-[#103080]/20 h-9 w-56" data-testid={`role-edit-input-${role.id}`} />
      <button type="submit" aria-label="Save role name" data-testid={`role-edit-save-${role.id}`}
        className="w-8 h-8 rounded-full bg-[#103080] text-white flex items-center justify-center hover:bg-[#0090D0] transition-colors duration-200"><Check size={14} /></button>
      <button type="button" onClick={() => setEditing(false)} aria-label="Cancel" data-testid={`role-edit-cancel-${role.id}`}
        className="w-8 h-8 rounded-full border border-[#103080]/20 text-[#103080] flex items-center justify-center hover:bg-[#103080]/5 transition-colors duration-200"><X size={14} /></button>
    </form>
  );
}

function RoleRow({ role, people, canEdit, onSave, onDelete }) {
  const toggle = (id) => {
    const ids = role.member_ids.includes(id) ? role.member_ids.filter((x) => x !== id) : [...role.member_ids, id];
    onSave({ ...role, member_ids: ids });
  };
  return (
    <li className="py-4 border-b border-[#103080]/10 last:border-0" data-testid={`role-row-${role.id}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <RoleName role={role} canEdit={canEdit} onSave={onSave} />
        {canEdit && (
          <button onClick={() => onDelete(role)} aria-label="Delete role" data-testid={`role-delete-${role.id}`}
            className="text-[#103080]/40 hover:text-red-600 transition-colors duration-200"><Trash2 size={14} /></button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {people.map((p) => {
          const on = role.member_ids.includes(p.id);
          return (
            <button key={p.id} disabled={!canEdit} onClick={() => toggle(p.id)} data-testid={`role-${role.id}-member-${p.id}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${on ? "bg-[#103080] text-white border-[#103080]" : "border-[#103080]/20 text-[#103080]/70 hover:border-[#0090D0]"} disabled:cursor-default`}>
              {p.name}
            </button>
          );
        })}
      </div>
    </li>
  );
}

export default function RolesCard() {
  const { user } = useAuth();
  const canEdit = user.role === "admin";
  const [roles, setRoles] = useState([]);
  const [people, setPeople] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/roles").then((r) => setRoles(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
    api.get("/directory").then((r) => setPeople(r.data)).catch(() => {});
  }, []);

  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };
  const add = (e) => {
    e.preventDefault();
    run(() => api.post("/roles", { name, member_ids: [] }), "Role added").then(() => setName(""));
  };

  return (
    <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-6 sm:p-8" data-testid="roles-card">
      <h2 className="font-serif-brand font-semibold text-2xl text-[#103080]">Team roles</h2>
      <p className="text-sm text-[#0F172A]/60 mt-1 mb-4">
        Put staff into roles and rename roles to suit your firm. When a template task is for a role, such as Preparer, it's automatically given to the first person in that role.
        {!canEdit && " Only an admin can change roles."}
      </p>
      <ul>
        {roles.map((r) => (
          <RoleRow key={r.id} role={r} people={people} canEdit={canEdit}
            onSave={(x) => run(() => api.put(`/roles/${x.id}`, { name: x.name, member_ids: x.member_ids }), x.name !== r.name ? "Role renamed" : null)}
            onDelete={(x) => window.confirm(`Delete role "${x.name}"?`) && run(() => api.delete(`/roles/${x.id}`), "Role deleted")} />
        ))}
      </ul>
      {canEdit && (
        <form onSubmit={add} className="flex gap-2 mt-4">
          <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="New role, e.g. Bookkeeper"
            className="rounded-xl bg-[#FDFBF7] border-[#103080]/20 h-11 max-w-xs" data-testid="role-name-input" />
          <button type="submit" data-testid="role-add-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#103080] text-white font-medium px-5 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200"><Plus size={15} /> Add role</button>
        </form>
      )}
    </section>
  );
}
